import { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth";
import { getGeocodingProgress } from "@/api/orders";
import { toastId, toastSuccess, toastWarning } from "@/lib/toast.tsx";
import { geocodeProgressAtom } from "@/store/geocodeProgress";
import {
  buildGeocodeFailureBreakdown,
  formatGeocodeFailureText,
  parseGeocodeFailureItems,
} from "@/features/orders/lib/geocodeFailureBreakdown";

/** Poll cadence, mirroring `useBuildRoutePoller` (BUG-410). */
export const GEOCODE_POLL_INTERVAL_MS = 2000;

/**
 * How long `pending` may sit unchanged before the banner gives up (BUG-410).
 *
 * Rationale for 2 minutes: the import fan-out staggers tasks ~1.2s apart, so
 * under normal load a live batch moves the needle every couple of seconds and
 * NEVER stalls this long — a 2-minute freeze means work is genuinely stuck, not
 * merely slow. The window is also comfortably longer than a Celery worker
 * restart plus queue drain, so a routine deploy mid-import does not trip it
 * spuriously. It only ever fires because of BUG-415: `geocode_order` has no
 * retry policy and only sets `geocode_failed` when the provider cleanly returns
 * empty, so a killed worker or a raising provider leaves the order with no
 * coordinates AND `geocode_failed = false` — permanently "pending". Polling
 * "until pending === 0" would hang forever; instead we stop and state the
 * unresolved count honestly.
 *
 * Note the timer measures time since `pending` last CHANGED, not since the
 * batch started, so a large slow import that keeps making progress is never cut
 * short however long it takes.
 */
export const GEOCODE_STALL_TIMEOUT_MS = 120_000;

/**
 * Global hook that tracks post-import address geocoding (BUG-410).
 * Mounted once at the AppShell level so the banner survives navigating away
 * from the Orders page (mirrors `useBuildRoutePoller`).
 *
 * Polling contract:
 *
 *   GET /v1/orders/geocoding-progress/?batch=<import_batch_id>
 *   → { total, pending, done, failed, progress_pct, status }
 *
 * `import_batch_id` is a uuid4 the import stamps on every order it created, so
 * the counts cover exactly that import — unlike a tenant-wide "orders without
 * coordinates" count, which also picks up hand-created orders (`signals.py`
 * geocodes those too), public-API orders, and a concurrent import's leftovers.
 *
 * Termination is two-sided:
 *  - `status === "completed"` (pending hit 0) → success/warning toast;
 *  - no change in `pending` for {@link GEOCODE_STALL_TIMEOUT_MS} → stop and
 *    report the unresolved remainder rather than spinning forever.
 */
export function useGeocodeProgressPoller(): void {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [task, setTask] = useAtom(geocodeProgressAtom);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Guards the terminal side-effects (toast + final invalidation) so they run
  // exactly once per batch even if the atom is re-set or the query re-emits the
  // same terminal payload (mirrors the BUG-178 guard in useBuildRoutePoller).
  const handledBatchIdRef = useRef<string | null>(null);

  // Stall detection: the last `pending` value seen and when it was first seen.
  // Reset whenever the batch id changes so a new import starts with a fresh
  // window.
  const stallRef = useRef<{
    batchId: string | null;
    pending: number | null;
    since: number;
  }>({ batchId: null, pending: null, since: 0 });

  const isTracking = task !== null && task.outcome === null;

  const pollingQuery = useQuery({
    queryKey: ["geocodingProgress", task?.batchId],
    queryFn: () => getGeocodingProgress(task!.batchId),
    // Never poll while logged out (BUG-175), and stop once the batch reached a
    // terminal state (completed or timed out).
    enabled: isTracking && isAuthenticated,
    refetchInterval: (query) =>
      query.state.data?.status === "completed"
        ? false
        : GEOCODE_POLL_INTERVAL_MS,
    retry: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (task === null || task.outcome !== null) return;

    // The progress endpoint failed (network/auth): drop the banner rather than
    // spinning on a stale denominator.
    if (pollingQuery.isError) {
      setTask(null);
      return;
    }

    const data = pollingQuery.data;
    if (!data) return;

    // A batch whose orders all already had coordinates reports total 0; the
    // banner is never seeded in that case, but treat it as done defensively.
    const isCompleted = data.status === "completed";

    // --- stall detection -----------------------------------------------
    const now = Date.now();
    if (stallRef.current.batchId !== task.batchId) {
      stallRef.current = {
        batchId: task.batchId,
        pending: data.pending,
        since: now,
      };
    } else if (stallRef.current.pending !== data.pending) {
      // Progress moved — restart the window.
      stallRef.current = {
        batchId: task.batchId,
        pending: data.pending,
        since: now,
      };
    }
    const stalled =
      !isCompleted && now - stallRef.current.since >= GEOCODE_STALL_TIMEOUT_MS;

    // Mirror the latest counts into the atom so the banner advances. Late
    // coordinates and `geocode_failed` badges land on the list rows themselves,
    // so refresh those too (this replaces the blind bounded timer the import
    // modal used to run).
    if (
      data.pending !== task.pending ||
      data.done !== task.done ||
      data.failed !== task.failed
    ) {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    }

    if (!isCompleted && !stalled) {
      setTask((prev) =>
        prev && prev.batchId === task.batchId
          ? {
              ...prev,
              total: data.total || prev.total,
              pending: data.pending,
              done: data.done,
              failed: data.failed,
            }
          : prev,
      );
      return;
    }

    // --- terminal ------------------------------------------------------
    if (handledBatchIdRef.current === task.batchId) return;
    handledBatchIdRef.current = task.batchId;

    void queryClient.invalidateQueries({ queryKey: ["orders"] });

    setTask((prev) =>
      prev && prev.batchId === task.batchId
        ? {
            ...prev,
            total: data.total || prev.total,
            pending: data.pending,
            done: data.done,
            failed: data.failed,
            outcome: isCompleted ? "completed" : "timeout",
          }
        : prev,
    );

    if (isCompleted) {
      if (data.failed > 0) {
        // TASK-4745: the bare count («3 адрес не удалось определить») named neither
        // WHICH addresses failed nor WHY, so the dispatcher had nothing to act on. The
        // count stays as the title — it is the honest untruncated total — and the
        // itemized breakdown rides as the description (TASK-4739). Persistent because
        // this is a work list: a 10s auto-dismiss would take the addresses off screen
        // before they could be read, let alone written down.
        const items = parseGeocodeFailureItems(data.failed_items);
        const breakdown = buildGeocodeFailureBreakdown(items, data.failed, t);
        toastWarning(
          t("toast.geocodeFailedCount", { count: data.failed }),
          items.length > 0 ? formatGeocodeFailureText(breakdown, t) : undefined,
          {
            persistent: true,
            id: toastId("warning", "geocode", "failed"),
          },
        );
      } else {
        toastSuccess(t("toast.geocodeCompleted", { count: data.done }));
      }
    } else {
      // Stalled: say exactly how many addresses never resolved instead of
      // implying everything finished.
      toastWarning(t("toast.geocodeStalled", { count: data.pending }));
    }
    // `dataUpdatedAt` — NOT `data` — is the trigger. A stuck batch returns a
    // byte-identical payload every poll, so React Query keeps the same object
    // reference and an effect keyed on `data` alone would never re-run; the
    // stall timeout could then never fire, which is exactly the hang this whole
    // mechanism exists to prevent (BUG-415).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pollingQuery.dataUpdatedAt,
    pollingQuery.isError,
    task?.batchId,
    task?.outcome,
  ]);
}
